import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AdminAPI } from '../../shared/api/admin-api';
import { useNotification } from '../../shared/contexts/NotificationContext';
import CategoryForm from './CategoryForm';
import AnimatedList from '../../shared/components/AnimatedList';
import AnimatedItem from '../../shared/components/AnimatedItem';
import LoadingSpinner from '../../shared/components/LoadingSpinner';

interface Category {
  id: number;
  name: string;
  description?: string;
  image?: string;
  product_count?: number;
}

function Categorias() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { success, error } = useNotification();

  // Verificar se há parâmetros de edição/criação
  const isEdit = searchParams.get('edit') !== null;
  const isNew = searchParams.get('new') === 'true';

  useEffect(() => {
    // Só carregar categorias se não estiver editando/criando
    if (!isEdit && !isNew) {
      loadCategories();
    }
  }, [isEdit, isNew]);

  const loadCategories = async () => {
    try {
      const categoriesData = await AdminAPI.get('categories') as Category[];
      setCategories(categoriesData);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (categoryId: number) => {
    navigate(`/admin/categorias?edit=${categoryId}`);
  };

  const handleDelete = async (categoryId: number) => {
    if (!confirm('Tem certeza que deseja excluir esta categoria?')) return;
    
    try {
      await AdminAPI.delete(`categories/${categoryId}`);
      success('Categoria excluída com sucesso!');
      await loadCategories();
    } catch (err: any) {
      console.error('Erro ao excluir categoria:', err);
      error(err.message || 'Erro ao excluir categoria');
    }
  };

  // Se estiver editando ou criando, mostrar o formulário (após todos os hooks)
  if (isEdit || isNew) {
    return (
      <div className="form-transition form-transition-entered">
        <CategoryForm />
      </div>
    );
  }

  return (
    <>
      {/* Loading Spinner - aparece apenas durante carregamento inicial */}
      {loading && <LoadingSpinner message="Carregando categorias..." />}
      
      {/* Categories List */}
      <section className="products-section">
        <AnimatedList
          listKey="categories"
          className="products-list"
        >
        {categories.length === 0 ? (
          <div>Nenhuma categoria encontrada</div>
        ) : (
          categories.map((category, index) => {
            // Verificar se image é um ícone (começa com "fa-") ou imagem
            const isIcon = category.image && typeof category.image === 'string' && category.image.startsWith('fa-');
            const isImage = category.image && !isIcon;
            
            return (
            <AnimatedItem
              key={category.id}
              className="product-item"
              index={index}
            >
              <div className="product-image-container">
                {isIcon ? (
                  <div className="product-icon-large">
                    <i className={`fas ${category.image}`}></i>
                  </div>
                ) : isImage ? (
                  <img
                    src={category.image}
                    alt={category.name}
                    className="product-image"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/assets/images/placeholder.jpg';
                    }}
                  />
                ) : (
                  <div className="product-icon-large">
                    <i className="fas fa-tags"></i>
                  </div>
                )}
              </div>
              <div className="product-info">
                <div className="product-name">{category.name}</div>
                <div className="product-details">
                  <span className="product-price" style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                    {category.product_count || 0} {category.product_count === 1 ? 'produto' : 'produtos'}
                  </span>
                </div>
              </div>
              <div className="product-actions">
                <button
                  className="action-btn edit"
                  onClick={() => handleEdit(category.id)}
                  title="Editar"
                >
                  <i className="fas fa-edit"></i>
                </button>
                <button
                  className="action-btn delete"
                  onClick={() => handleDelete(category.id)}
                  title="Excluir"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </AnimatedItem>
            );
          })
        )}
      </AnimatedList>
    </section>
    </>
  );
}

export default Categorias;

